import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import api, { apiErrorText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { fmtDate } from "../lib/format";
import {
  ChevronLeft,
  ChevronRight,
  Coins,
  Pencil,
  Plus,
  Search,
  Settings2,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

const fmtUSD = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });

const fmtCoin = (n) =>
  Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 4 });

const todayISO = () => new Date().toISOString().slice(0, 10);
const currentMonth = () => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const monthAdd = (ym, n) => {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (ym) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
};

const EMPTY_FORM = {
  date: todayISO(),
  amount_usd: "",
  coin_amount: "",
  category: "",
  paid_to: "",
  description: "",
  notes: "",
};

export default function CashCoinPayments() {
  const [month, setMonth] = useState(currentMonth());
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [catOpen, setCatOpen] = useState(false);
  const [catDraft, setCatDraft] = useState([]);
  const [catInput, setCatInput] = useState("");

  const monthRange = useMemo(() => {
    const [y, m] = month.split("-").map(Number);
    const start = `${month}-01`;
    const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
    return { from: start, to: `${month}-${String(last).padStart(2, "0")}` };
  }, [month]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { from: monthRange.from, to: monthRange.to };
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (query.trim()) params.q = query.trim();
      const [list, sum, cats] = await Promise.all([
        api.get("/cash-coin-payments", { params }),
        api.get("/cash-coin-payments/summary", { params: { month } }),
        api.get("/cash-coin-payments/categories"),
      ]);
      setItems(list.data);
      setSummary(sum.data);
      setCategories(cats.data.categories || []);
    } catch (e) {
      toast.error(apiErrorText(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [month, categoryFilter, query]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      date: monthRange.to >= todayISO() ? todayISO() : monthRange.to,
    });
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      date: item.date || todayISO(),
      amount_usd: String(item.amount_usd || ""),
      coin_amount: String(item.coin_amount || ""),
      category: item.category || "",
      paid_to: item.paid_to || "",
      description: item.description || "",
      notes: item.notes || "",
    });
    setOpen(true);
  };

  const save = async () => {
    const amountUsd = Number(form.amount_usd);
    const coinAmount = form.coin_amount === "" ? 0 : Number(form.coin_amount);
    if (!amountUsd || amountUsd <= 0) return toast.error("Enter a valid USD amount");
    if (coinAmount < 0) return toast.error("Coin amount cannot be negative");
    const payload = {
      date: form.date,
      amount_usd: amountUsd,
      coin_amount: coinAmount,
      category: form.category || null,
      paid_to: form.paid_to || null,
      description: form.description || null,
      notes: form.notes || null,
    };
    try {
      if (editing) {
        await api.put(`/cash-coin-payments/${editing.id}`, payload);
        toast.success("Payment updated");
      } else {
        await api.post("/cash-coin-payments", payload);
        toast.success("Payment added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete payment of ${fmtUSD(item.amount_usd)}?`)) return;
    try {
      await api.delete(`/cash-coin-payments/${item.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const openCats = () => {
    setCatDraft([...categories]);
    setCatInput("");
    setCatOpen(true);
  };

  const addCatDraft = () => {
    const value = catInput.trim();
    if (!value) return;
    if (catDraft.some((c) => c.toLowerCase() === value.toLowerCase())) {
      return toast.message(`"${value}" already exists`);
    }
    setCatDraft([...catDraft, value]);
    setCatInput("");
  };

  const saveCats = async () => {
    try {
      await api.put("/cash-coin-payments/categories", { categories: catDraft });
      toast.success("Categories updated");
      setCatOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const sortedCategories = Object.entries(summary?.by_category || {}).sort(
    (a, b) => (b[1].amount_usd || 0) - (a[1].amount_usd || 0)
  );
  const sortedPaidTo = Object.entries(summary?.by_paid_to || {}).sort(
    (a, b) => (b[1].amount_usd || 0) - (a[1].amount_usd || 0)
  );

  return (
    <Layout
      title="Cash Coin Payment"
      subtitle="Save cash coin payment records and track them monthly"
      testId="cash-coin-payments-page"
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={openCats} className="h-9">
            <Settings2 size={14} className="mr-1.5" /> Categories
          </Button>
          <Button onClick={openCreate} className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white">
            <Plus size={14} className="mr-1.5" /> Add payment
          </Button>
        </div>
      }
    >
      <div className="stat-card p-3 mb-5 flex flex-wrap items-center gap-2">
        <button onClick={() => setMonth(monthAdd(month, -1))} className="h-8 w-8 rounded-md border hairline bg-white hover:bg-zinc-50 flex items-center justify-center">
          <ChevronLeft size={14} strokeWidth={1.7} />
        </button>
        <div className="font-display text-[15px] font-semibold tracking-tight px-2">
          {monthLabel(month)}
        </div>
        <button onClick={() => setMonth(monthAdd(month, 1))} className="h-8 w-8 rounded-md border hairline bg-white hover:bg-zinc-50 flex items-center justify-center">
          <ChevronRight size={14} strokeWidth={1.7} />
        </button>
        {month !== currentMonth() && (
          <button onClick={() => setMonth(currentMonth())} className="h-8 px-3 rounded-md text-[12.5px] border hairline bg-white hover:bg-zinc-50">
            This month
          </button>
        )}
        <div className="relative flex-1 min-w-[220px] ml-auto">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-8 pl-8"
            placeholder="Search paid to, category, note"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="h-8 w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="stat-card p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">Total USD</div>
          <div className="font-mono text-[22px] font-medium mt-1 text-amber-700">
            {fmtUSD(summary?.total_usd)}
          </div>
        </div>
        <div className="stat-card p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">Total coin</div>
          <div className="font-mono text-[22px] font-medium mt-1">
            {fmtCoin(summary?.total_coin)}
          </div>
        </div>
        <div className="stat-card p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">Entries</div>
          <div className="font-mono text-[22px] font-medium mt-1">{summary?.count || 0}</div>
        </div>
        <div className="stat-card p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">Top category</div>
          <div className="font-display text-[16px] font-semibold mt-1 truncate">
            {sortedCategories[0]?.[0] || "-"}
          </div>
          {sortedCategories[0] && (
            <div className="text-[10.5px] text-zinc-500 mt-0.5 font-mono">
              {fmtUSD(sortedCategories[0][1].amount_usd)}
            </div>
          )}
        </div>
      </div>

      {summary?.count > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
          <Breakdown title="By category" rows={sortedCategories} />
          <Breakdown title="By paid to" rows={sortedPaidTo.slice(0, 8)} />
        </div>
      )}

      <div className="stat-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                <th className="py-3 px-4 font-medium">Date</th>
                <th className="py-3 px-4 font-medium">Category</th>
                <th className="py-3 px-4 font-medium">Paid to</th>
                <th className="py-3 px-4 font-medium">Description</th>
                <th className="py-3 px-4 font-medium text-right">Coin</th>
                <th className="py-3 px-4 font-medium text-right">USD</th>
                <th className="py-3 px-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {loading ? (
                <tr><td colSpan={7} className="py-8 text-center text-zinc-500">Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-zinc-500">
                    <Coins size={20} strokeWidth={1.5} className="mx-auto mb-2 text-zinc-400" />
                    No cash coin payments for {monthLabel(month)}.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} className="row-hover">
                    <td className="py-3 px-4 font-mono text-[12.5px]">{fmtDate(item.date)}</td>
                    <td className="py-3 px-4">
                      {item.category ? (
                        <span className="inline-flex text-[11px] px-1.5 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-800">
                          {item.category}
                        </span>
                      ) : (
                        <span className="text-zinc-400">Uncategorized</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-[12.5px]">{item.paid_to || "-"}</td>
                    <td className="py-3 px-4 max-w-[280px]">
                      <div className="truncate font-medium">{item.description || <span className="text-zinc-400">-</span>}</div>
                      {item.notes && <div className="text-[11.5px] text-zinc-500 truncate">{item.notes}</div>}
                    </td>
                    <td className="py-3 px-4 font-mono text-right">{fmtCoin(item.coin_amount)}</td>
                    <td className="py-3 px-4 font-mono text-right text-amber-700">{fmtUSD(item.amount_usd)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(item)} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600">
                          <Pencil size={14} strokeWidth={1.6} />
                        </button>
                        <button onClick={() => remove(item)} className="p-1.5 rounded hover:bg-rose-50 text-rose-600">
                          <Trash2 size={14} strokeWidth={1.6} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit payment" : "Add cash coin payment"}</DialogTitle>
            <DialogDescription>Save the payment amount, coin amount, category, and monthly record.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Date *</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Amount USD *</Label>
              <Input type="number" step="0.01" value={form.amount_usd} onChange={(e) => setForm({ ...form, amount_usd: e.target.value })} placeholder="0.00" />
            </div>
            <div className="space-y-1.5">
              <Label>Coin amount</Label>
              <Input type="number" step="0.0001" value={form.coin_amount} onChange={(e) => setForm({ ...form, coin_amount: e.target.value })} placeholder="0" />
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <Select value={form.category || "__none"} onValueChange={(v) => setForm({ ...form, category: v === "__none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">- None -</SelectItem>
                  {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Paid to / source</Label>
              <Input value={form.paid_to} onChange={(e) => setForm({ ...form, paid_to: e.target.value })} placeholder="Optional" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional" />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-zinc-900 hover:bg-zinc-800 text-white">
              {editing ? "Save changes" : "Add payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Cash coin categories</DialogTitle>
            <DialogDescription>Use categories to track monthly spending by purpose.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-1.5">
            <Input
              value={catInput}
              onChange={(e) => setCatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCatDraft();
                }
              }}
              placeholder="e.g. Vendor"
              className="h-9"
            />
            <Button onClick={addCatDraft} className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white">
              <Plus size={13} />
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {catDraft.map((c, i) => (
              <span key={`${c}-${i}`} className="inline-flex items-center gap-1 text-[12px] border hairline rounded px-1.5 py-0.5 bg-zinc-50">
                {c}
                <button onClick={() => setCatDraft(catDraft.filter((_, idx) => idx !== i))} className="text-zinc-400 hover:text-rose-600">
                  <X size={11} />
                </button>
              </span>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatOpen(false)}>Cancel</Button>
            <Button onClick={saveCats} className="bg-zinc-900 hover:bg-zinc-800 text-white">Save categories</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Breakdown({ title, rows }) {
  return (
    <div className="stat-card p-4">
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-3">{title}</div>
      <div className="space-y-1.5">
        {rows.map(([name, info]) => (
          <div key={name} className="flex items-center justify-between text-[12.5px] border-b hairline pb-1.5 last:border-0">
            <span className="font-medium truncate">{name}</span>
            <span className="font-mono text-amber-700">
              {fmtUSD(info.amount_usd)}
              <span className="text-[10.5px] text-zinc-400 ml-1.5">
                {fmtCoin(info.coin_amount)} coin · {info.count}
              </span>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
